import { Money } from "../common/money";

/** Mirrors ReconcilliationJobHistoryResponse returned by the gateway. */
export interface ReconcilliationJobHistoryData {
	id: string;
	timestamp: string;
	/** The gateway deserializes the stored JSON, so this arrives as an object. */
	data: ReconcilliationResult;
}

/** Mirrors ReconcilliationJob.Contracts.ReconcilliationResult. */
export interface ReconcilliationResult {
	date: string;
	settledToday: Money;
	deliveredToday: Money;
	openingOwed: Money;
	closingOwedCalculated: Money;
	closingOwedMeasured: Money;
	variance: Money;
	changes: ReconcilliationChanges;
	backlogAging: BacklogAging;
	exceptions: ReconcilliationExceptions;
}

/**
 * Whole-percent movement of each summary figure against the previous day's report.
 * 100 when there is no previous report to compare against.
 */
export interface ReconcilliationChanges {
	settledToday: number;
	deliveredToday: number;
	owedBacklog: number;
	variance: number;
}

export interface BacklogAging {
	zeroToOneDay: Money;
	twoToThreeDays: Money;
	fourPlusDays: Money;
	/** The stuck rows behind fourPlusDays, oldest first. Absent on reports written before the table existed. */
	fourPlusDaysTransactionIds: string[];
}

export interface ReconcilliationExceptions {
	paymentHubFalsePositives: ExceptionLine;
	/** Stored by the job but deliberately not shown on the dashboard yet. */
	paymentHubFalseNegatives: ExceptionLine;
	doubleVends: ExceptionLine;
	refundedAfterDelivery: RefundedAfterDelivery;
	lateCompletions: LateCompletions;
}

export interface ExceptionLine {
	count: number;
	amount: Money;
}

/** The job stores only the ids; the details tab fetches the rows a page at a time. */
export interface RefundedAfterDelivery extends ExceptionLine {
	transactionIds: string[];
}

export interface LateCompletions extends RefundedAfterDelivery {
	averageDays: number;
}

/** One row of a details-tab table. Mirrors Gateway's ReconcilliationTransactionDetail. */
export interface ReconcilliationTransactionDetail {
	id: string;
	reference: string;
	externalReference?: string | null;
	vendingTransactionId?: string | null;
	amount: Money;
	createdAt: string;
	vendedAt?: string | null;
	refundedAt?: string | null;
	meterNumber: string;
	/** Last vend attempt of any outcome; null on bills, which keep no attempt trail. */
	lastAttemptAt?: string | null;
	/** Shared.Domain.Enums.VendingStatus, crossing the wire as its numeric value. */
	lastAttemptStatus?: number | null;
}

/** One page of every details-tab table, as returned by a single transaction-details request. */
export interface ReconcilliationTransactionDetails {
	refundedAfterDelivery: ReconcilliationTransactionDetail[];
	lateCompletions: ReconcilliationTransactionDetail[];
	stuckBacklog: ReconcilliationTransactionDetail[];
}

/** The ids each table is currently showing, one page's worth per list. */
export interface ReconcilliationTransactionDetailsRequest {
	refundedTransactionIds: string[];
	lateTransactionIds: string[];
	stuckTransactionIds: string[];
}

/**
 * Money crosses the wire as a plain object, so it is rehydrated into the Money class to make
 * toLocaleString() available on every figure.
 */
export const parseReconcilliationResult = (historyData: ReconcilliationJobHistoryData): ReconcilliationResult => {
	const parsed = historyData.data;

	return {
		...parsed,
		settledToday: new Money(parsed.settledToday),
		deliveredToday: new Money(parsed.deliveredToday),
		openingOwed: new Money(parsed.openingOwed),
		closingOwedCalculated: new Money(parsed.closingOwedCalculated),
		closingOwedMeasured: new Money(parsed.closingOwedMeasured),
		variance: new Money(parsed.variance),
		backlogAging: {
			zeroToOneDay: new Money(parsed.backlogAging.zeroToOneDay),
			twoToThreeDays: new Money(parsed.backlogAging.twoToThreeDays),
			fourPlusDays: new Money(parsed.backlogAging.fourPlusDays),
			fourPlusDaysTransactionIds: parsed.backlogAging.fourPlusDaysTransactionIds ?? [],
		},
		exceptions: {
			paymentHubFalsePositives: {
				...parsed.exceptions.paymentHubFalsePositives,
				amount: new Money(parsed.exceptions.paymentHubFalsePositives.amount),
			},
			paymentHubFalseNegatives: {
				...parsed.exceptions.paymentHubFalseNegatives,
				amount: new Money(parsed.exceptions.paymentHubFalseNegatives.amount),
			},
			doubleVends: {
				...parsed.exceptions.doubleVends,
				amount: new Money(parsed.exceptions.doubleVends.amount),
			},
			refundedAfterDelivery: {
				...parsed.exceptions.refundedAfterDelivery,
				amount: new Money(parsed.exceptions.refundedAfterDelivery.amount),
			},
			lateCompletions: {
				...parsed.exceptions.lateCompletions,
				amount: new Money(parsed.exceptions.lateCompletions.amount),
			},
		},
	};
};
